<template>
  <RevCard :aria-label="props.countryCode" class="p-28">
    <div class="heading-2 mb-6 flex items-center">
      <Flag class="mr-12" :country-code="props.countryCode" size="l" />

      <span>{{ i18n.country(props.countryCode) }}</span>
    </div>

    <div
      v-if="!shouldShowPricingRuleForm && props.pricingRules.length"
      class="space-y-12"
    >
      <RevToggle
        v-model="automaticUpdatesEnabled"
        :label="i18n(translations.dailyUpdate)"
        @update:model-value="handleOnAutomaticUpdatesChange"
      >
        {{ i18n(translations.dailyUpdateDescription) }}
      </RevToggle>

      <div
        v-if="metadata.withoutBackboxOnlyDisabledMarkets"
        class="flex items-center space-x-4"
      >
        <IconInfo />
        <span>
          {{ i18n(translations.withoutBackBoxesInfo) }}
        </span>
      </div>

      <PricingRulesList
        :pricing-rules="props.pricingRules"
        @edit="handleOnEdit"
        @update="emit('update')"
      />

      <RevButton variant="secondary" @click="shouldShowPricingRuleForm = true">
        {{ i18n(translations.addAnotherRule) }}
      </RevButton>
    </div>

    <PricingRuleForm
      v-if="shouldShowPricingRuleForm || !props.pricingRules.length"
      :categories="metadata.categories"
      :fixed-multiplier="metadata.fixedMultiplier"
      :has-rules="Boolean(pricingRules.length)"
      :rule-to-edit
      :targetMarket="props.countryCode"
      @cancel="handleOnCancel"
      @submit="handleOnSubmit"
    />
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { ref } from 'vue'

import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import {
  type PricingRule,
  patchMerchantConfiguration,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants'
import Flag from '@backmarket/nuxt-layer-base/components/Flag/Flag.vue'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevToggle } from '@ds/components/Toggle'
import { IconInfo } from '@ds/icons/IconInfo'

import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'

import type { FormattedPricingRuleMetadata } from '../../Pricing.types'

import translations from './MarketPricingRule.translations'
import PricingRuleForm from './components/PricingRuleForm/PricingRuleForm.vue'
import PricingRulesList from './components/PricingRulesList/PricingRulesList.vue'

const i18n = useI18n()
const { trackClick } = useTracking()
const { openErrorToast } = useTheToast()
const sellerStore = useSellerInfoStore()

const emit = defineEmits(['update'])
const props = defineProps<{
  countryCode: MarketCountryCode
  metadata: FormattedPricingRuleMetadata
  pricingRules: PricingRule[]
}>()

const shouldShowPricingRuleForm = ref(false)
const ruleToEdit = ref<PricingRule | null>(null)
const automaticUpdatesEnabled = ref(props.metadata.automaticUpdatesEnabled)

function handleOnSubmit() {
  shouldShowPricingRuleForm.value = false
  emit('update')
}

function handleOnEdit(rule: PricingRule) {
  ruleToEdit.value = rule
  shouldShowPricingRuleForm.value = true
}

function handleOnCancel() {
  ruleToEdit.value = null
  shouldShowPricingRuleForm.value = false
}

async function handleOnAutomaticUpdatesChange() {
  try {
    trackClick({
      zone: 'pricing_tool',
      name: automaticUpdatesEnabled.value
        ? 'auto_pricing_on'
        : 'auto_pricing_off',
      value: {
        sourceMarket: sellerStore.referenceMarket,
        targetMarket: props.countryCode,
      },
    })

    await $httpFetch(patchMerchantConfiguration, {
      body: {
        markets: [
          {
            market: props.countryCode,
            automaticUpdatesEnabled: automaticUpdatesEnabled.value,
            updateOnlyListingsWithoutBackbox:
              props.metadata.updateOnlyListingsWithoutBackbox,
          },
        ],
      },
    })
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  }
}
</script>
